import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Trash, PencilSimple, FloppyDisk, UploadSimple, Image } from '@phosphor-icons/react';
import { API, BACKEND_URL } from './adminConstants';

export function ServicesAdminManagement() {
  const { getAuthHeader } = useAuth();
  const [services, setServices] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editService, setEditService] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', icon: 'Recycle', icon_url: '', image_url: '', features: '', order: 0 });
  const [uploading, setUploading] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);

  const iconOptions = ['Recycle', 'Truck', 'Broom', 'Wrench', 'Road', 'Factory'];
  const load = async () => { try { const r = await axios.get(`${API}/services`); setServices(r.data); } catch { toast.error('Failed'); } };
  useEffect(() => { load(); }, []);
  const resetForm = () => { setForm({ title: '', description: '', icon: 'Recycle', icon_url: '', image_url: '', features: '', order: services.length }); setEditService(null); setShowForm(false); };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return; setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try { const res = await axios.post(`${API}/admin/services/upload-image`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }); setForm(prev => ({ ...prev, image_url: `${BACKEND_URL}${res.data.image_url}` })); toast.success('Uploaded'); } catch { toast.error('Upload failed'); }
    setUploading(false); e.target.value = '';
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return; setIconUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/services/upload-image`, fd, { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, icon_url: `${BACKEND_URL}${res.data.image_url}` }));
      toast.success('Custom icon uploaded');
    } catch { toast.error('Icon upload failed'); }
    setIconUploading(false); e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    const payload = { ...form, id: form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 50), features: form.features ? form.features.split('\n').filter(Boolean) : [] };
    try {
      if (editService) { await axios.put(`${API}/admin/services/${editService.id}`, payload, { headers: getAuthHeader() }); toast.success('Updated'); }
      else { await axios.post(`${API}/admin/services`, payload, { headers: getAuthHeader() }); toast.success('Created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (s) => { setEditService(s); setForm({ title: s.title || '', description: s.description || '', icon: s.icon || 'Recycle', icon_url: s.icon_url || '', image_url: s.image_url || '', features: (s.features || []).join('\n'), order: s.order || 0 }); setShowForm(true); };
  const handleDelete = async (id) => { try { await axios.delete(`${API}/admin/services/${id}`, { headers: getAuthHeader() }); toast.success('Deleted'); load(); } catch { toast.error('Failed'); } };

  return (
    <div data-testid="services-admin">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-outfit font-bold text-2xl text-slate-900">Services</h1><p className="text-slate-500 text-sm">Manage services shown on the website (Services page &amp; Home page)</p></div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-lime-600 hover:bg-lime-700" data-testid="create-service-btn"><Plus size={15} className="mr-1" />Add Service</Button>
      </div>
      {showForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6" data-testid="service-form">
          <h3 className="font-semibold text-slate-900 mb-4">{editService ? 'Edit Service' : 'New Service'}</h3>
          <div className="space-y-4">
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Commercial Waste Collections" data-testid="service-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Service description..." rows={3} data-testid="service-desc-input" /></div>

            {/* Icon Section */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3 border border-slate-100">
              <div className="flex items-center gap-2 mb-1"><Image size={16} className="text-slate-500" /><Label className="text-xs font-semibold text-slate-700">Service Icon</Label></div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] text-slate-500 mb-1 block">Default Icon</Label>
                  <Select value={form.icon} onValueChange={v => setForm({ ...form, icon: v })}>
                    <SelectTrigger data-testid="service-icon-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{iconOptions.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                  </Select>
                  {!form.icon_url && <p className="text-[10px] text-slate-400 mt-1">Currently using default: <span className="font-medium">{form.icon}</span></p>}
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500 mb-1 block">Custom Icon (overrides default)</Label>
                  <div className="flex gap-2 items-start">
                    <label className="cursor-pointer flex-1">
                      <Button variant="outline" size="sm" asChild disabled={iconUploading} className="w-full">
                        <span><UploadSimple size={14} className="mr-1" />{iconUploading ? 'Uploading...' : 'Upload Custom Icon'}</span>
                      </Button>
                      <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" className="hidden" onChange={handleIconUpload} data-testid="service-icon-upload" />
                    </label>
                    {form.icon_url && (
                      <Button variant="outline" size="sm" onClick={() => setForm({ ...form, icon_url: '' })} className="text-red-500 border-red-200 hover:bg-red-50 shrink-0" data-testid="remove-custom-icon">
                        <Trash size={14} />
                      </Button>
                    )}
                  </div>
                  {form.icon_url && (
                    <div className="mt-2 flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg border border-slate-200 bg-white flex items-center justify-center p-1.5 overflow-hidden">
                        <img src={form.icon_url} alt="Custom icon" className="w-full h-full object-contain" />
                      </div>
                      <div>
                        <p className="text-[10px] text-lime-600 font-medium">Custom icon active</p>
                        <p className="text-[10px] text-slate-400">This overrides the default icon</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Preview */}
              <div className="flex items-center gap-3 pt-2 border-t border-slate-200">
                <span className="text-[10px] text-slate-500">Preview:</span>
                <div className="inline-flex p-2.5 rounded-xl bg-lime-50 border border-lime-100">
                  {form.icon_url ? (
                    <img src={form.icon_url} alt="Icon" className="w-8 h-8 object-contain" />
                  ) : (
                    <span className="text-xs text-lime-600 font-medium w-8 h-8 flex items-center justify-center">{form.icon?.charAt(0)}</span>
                  )}
                </div>
                <span className="text-[10px] text-slate-400">{form.icon_url ? 'Custom icon' : `Default: ${form.icon}`}</span>
              </div>
            </div>

            <div><Label className="text-xs">Display Order</Label><Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} min="0" /></div>
            <div><Label className="text-xs">Service Image</Label>
              <div className="flex gap-3 items-start"><div className="flex-1"><Input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} placeholder="Paste URL or upload..." /></div>
                <label className="cursor-pointer shrink-0"><Button variant="outline" size="sm" asChild disabled={uploading}><span><UploadSimple size={15} className="mr-1" />{uploading ? 'Uploading...' : 'Upload Photo'}</span></Button><input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="service-image-upload" /></label>
              </div>
              {form.image_url && <div className="mt-2 w-40 h-24 rounded-lg overflow-hidden border border-slate-200"><img src={form.image_url} alt="Preview" className="w-full h-full object-cover" /></div>}
            </div>
            <div><Label className="text-xs">Features (one per line)</Label><Textarea value={form.features} onChange={e => setForm({ ...form, features: e.target.value })} placeholder="Feature 1\nFeature 2\n..." rows={4} className="font-mono text-sm" /></div>
          </div>
          <div className="flex gap-3 mt-5"><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSave} className="bg-lime-600 hover:bg-lime-700" data-testid="save-service-btn"><FloppyDisk size={15} className="mr-1" />{editService ? 'Update' : 'Create'}</Button></div>
        </div>
      )}
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="divide-y divide-slate-100">
          {services.map(s => (
            <div key={s.id} className="p-4 flex items-center gap-4" data-testid={`service-item-${s.id}`}>
              <div className="w-10 h-10 rounded-lg bg-lime-50 border border-lime-100 flex items-center justify-center shrink-0 overflow-hidden p-1">
                {s.icon_url ? (
                  <img src={s.icon_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-xs text-lime-600 font-semibold">{(s.icon || 'R').charAt(0)}</span>
                )}
              </div>
              {s.image_url && <div className="w-20 h-14 rounded-lg overflow-hidden shrink-0 border border-slate-200"><img src={s.image_url} alt="" className="w-full h-full object-cover" /></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-slate-900">{s.title}</p>
                  {s.icon_url ? <span className="text-[10px] px-1.5 py-0.5 bg-lime-100 text-lime-700 rounded">Custom Icon</span> : <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">{s.icon}</span>}
                </div>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{s.description}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button variant="outline" size="sm" onClick={() => startEdit(s)}><PencilSimple size={14} className="mr-1" />Edit</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(s.id)} className="text-red-500 border-red-200 hover:bg-red-50"><Trash size={14} /></Button>
              </div>
            </div>
          ))}
          {services.length === 0 && <div className="p-12 text-center text-slate-500">No services yet. Add your first!</div>}
        </div>
      </div>
    </div>
  );
}
